<template>
  <transition name="overlayFade" appear>
    <stacked-cards-overlay
      v-if="showAlertsOverlay"
      ref="alertsOverlay"
      :items="activeAlertInstances"
      :show-buttons="showAlertsOverlayButtons"
      :show-primary-action-button="showPrimaryActionButton"
      :show-secondary-action-button="showSecondaryActionButton"
      :primary-action-button-text="primaryActionButtonText"
      :cancel-action-button-text="cancelActionButtonText"
      :cancel-action-callback="cancelActionCallback"
      :primary-action-callback="primaryActionCallback"
      @handleItemProcessed="handleItemProcessed"
      @clearAll="clearAll"
    />
  </transition>
</template>

<script>
import WebPubSubService from "@/components/WebPubSubService";
import StackedCardsOverlay from "@/components/ui/stackablecards/StackedCardsOverlay";
import { mapActions, mapGetters, mapMutations } from "vuex";
import RouteService from "@/router/RouteService";

/*
  To uncomment as soon as InPilot is able to call the `Mark as Completed` endpoint
  This commented code would be used to hide the `Mark as Completed` button for WxForms.
 */
// const wxFormHostPattern = /.*.inpilot.ca$/;
// function isWxForm(actionUrl) {
//   if (actionUrl) {
//     const hostname = URL.parse(actionUrl).hostname;
//     return wxFormHostPattern.test(hostname);
//   } else {
//     return false;
//   }
// }

export default {
  components: { StackedCardsOverlay },
  name: "AlertInstanceOverlay",
  data() {
    return {
      notificationGroupKey: this.notificationGroupingKey,
    };
  },
  methods: {
    ...mapActions("alerts", [
      "fetchAlertInstances",
      "ignoreAlertInstance",
      "completeAlertInstance",
      "ignoreAllAlertInstances",
      "setBufferingState",
      "startAutoIgnore",
      "stopAutoIgnore",
      "fetchAlerts",
    ]),
    ...mapMutations("alerts", ["setActiveAlertInstance"]),
    ...mapActions("dashboard", ["setActiveProductionUnitId"]),
    primaryActionCallback(instance) {
      if (this.isEndOfShiftReport(instance)) {
        return "redirect";
      } else {
        return "markAsCompleted";
      }
    },
    primaryActionButtonText(instance) {
      if (this.isEndOfShiftReport(instance)) {
        return this.$t("alerts.buttons.navigate");
      } else {
        return this.$t("alerts.buttons.markAsCompleted");
      }
    },
    isEndOfShiftReport(instance) {
      if (!instance) {
        return false;
      }
      return (
        this.alerts?.filter((alert) => alert.type === "EndOfShiftReport" && alert.id === instance.alertId)?.length > 0
      );
    },
    isEndOfShift(instance) {
      if (!instance) {
        return false;
      }
      return this.alerts?.filter((alert) => alert.type === "EndOfShift" && alert.id === instance.alertId)?.length > 0;
    },
    notificationGroupingKey(item) {
      return `${item.productionUnitId}:${item.alertId}`;
    },
    handleItemProcessed(action, payload) {
      const alertPayload = payload.item;
      switch (action) {
        case "ignore":
          this.ignoreAlertInstance({ alertPayload: alertPayload });
          break;
        case "markAsCompleted":
          this.completeAlertInstance({ alertPayload: alertPayload });
          break;
        case "redirect":
          this.setActiveAlertInstance({ instance: alertPayload });
          try {
            this.redirect({ alertPayload: alertPayload });
          } catch (error) {
            console.log(error);
          }
          break;
        default:
          console.warn("An invalid action type was submitted.");
          break;
      }
    },
    clearAll() {
      this.ignoreAllAlertInstances();
    },
    redirect({ alertPayload: alertPayload }) {
      if (this.canRedirect(alertPayload)) {
        this.setActiveProductionUnitId(alertPayload.productionUnitId);
        this.$router.push(RouteService.toDashboard(alertPayload.productionUnitId));
      }
    },
    canRedirect(alertPayload) {
      const currentProductionUnitId = this.activeProductionUnit.id;
      return (
        currentProductionUnitId !== alertPayload.productionUnitId || this.$router.currentRoute.name !== "Dashboard"
      );
    },
    attachSubElementHoverListeners() {
      const childRootEl = this.$refs.alertsOverlay?.$el;
      if (!childRootEl) return;
      const subElements = childRootEl.querySelectorAll(".group-stack");
      if (!subElements.length) return;

      // Store references so we can remove listeners later
      this._attachedDivs = Array.from(subElements);

      // 3) Attach event listeners to each element
      this._attachedDivs.forEach((el) => {
        el.addEventListener("mouseenter", this.onMouseEnter);
        el.addEventListener("mouseleave", this.onMouseLeave);
      });
    },
    detachSubElementHoverListeners() {
      if (this._attachedDivs && this._attachedDivs.length) {
        this._attachedDivs.forEach((el) => {
          el.removeEventListener("mouseenter", this.onMouseEnter);
          el.removeEventListener("mouseleave", this.onMouseLeave);
        });
      }
    },
    onMouseEnter() {
      this.setBufferingState({ isBuffering: true });
    },
    onMouseLeave() {
      this.setBufferingState({ isBuffering: false });
    },
    showPrimaryActionButton(instance) {
      /*
          Comment this code until InPilot is able to call the `Mark as Completed API
          and make the button useless (if the form is a WxForm of course
      */
      //return !isWxForm(instance.actionUrl);
      return !this.isEndOfShift(instance);
    },
    showSecondaryActionButton(instance) {
      return instance;
    },
  },
  computed: {
    ...mapGetters("user", ["isLoggedIn", "isPresenter"]),
    ...mapGetters("dashboard", ["activeProductionUnit" ]),
    ...mapGetters("navigation", ["activeFactory"]),
    ...mapGetters("alerts", ["activeAlertInstances", "alerts"]),
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    showAlertsOverlayButtons() {
      return !this.isPresenter;
    },
    showAlertsOverlay() {
      if (!this.activeAlertInstances) {
        return false;
      }
      const currentRoute = this.$route.name?.toLowerCase();
      const nonOverlayableRoutes = ["root", "home", "styleguide", "termsofservice"];
      return this.activeAlertInstances.size > 0 && !nonOverlayableRoutes.includes(currentRoute);
    },
    cancelActionButtonText() {
      return this.$t("alerts.buttons.ignore");
    },
    cancelActionCallback() {
      return "ignore";
    },
  },
  watch: {
    activeFactory() {
      const factoryId = this.activeFactory?.id;
      if (factoryId) {
        this.fetchAlerts({ factoryId: factoryId });
        WebPubSubService.disconnect();
        WebPubSubService.setupWebSocket();
        this.fetchAlertInstances();
      }
    },
  },
  mounted() {
    this.attachSubElementHoverListeners();
    this.startAutoIgnore();
  },
  beforeDestroy() {
    this.detachSubElementHoverListeners();
    this.stopAutoIgnore();
  },
};
</script>
